/**
 * Displays user notifications, including unread alerts and activity
 * updates. Supports notification retrieval, unread status tracking,
 * and direct access to Fluxx for additional details.
 */
import { LinkExternal01 } from "@untitledui/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { buildUrl, paths } from "@/api/url";
import { http } from "@/lib/http";
import { selectNotificationUrl2 } from "@/store/adminSlice";
import { selectTheme } from "@/store/theme";
import { selectFluxxUserId, selectSelectedRole } from "@/store/userSlice";

interface Notification {
  id: string;
  message: string;
  time: string;
  status: string;
  unread?: boolean;
}

const NotificationsDropdown: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const selectedRole = useSelector(selectSelectedRole);
  const selectedFluxxUserId = useSelector(selectFluxxUserId);
  const theme = useSelector(selectTheme);
  const FluxxURL = useSelector(selectNotificationUrl2);

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);

      const res = await http.post(buildUrl(paths.v1.notifications()), {
        roleId: selectedRole,
        userId: selectedFluxxUserId,
      });
      setNotifications(res.data?.data ?? []);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedFluxxUserId || !selectedRole) return;
    fetchNotifications();
  }, [selectedFluxxUserId, selectedRole]);

  const unreadCount = notifications.filter((n) => n.unread).length;

  return (
    <div
      className="w-full max-w-md bg-white rounded-xl border border-gray-200 shadow-lg flex flex-col"
      role="region"
      aria-label={t("components.navBar.notifications")}
    >
      <div className="px-4 pt-3 pb-2 flex items-center justify-between">
        <h2 className="text-md font-medium text-gray-950">
          {t("components.navBar.notifications")}
        </h2>

        {unreadCount > 0 && (
          <span
            className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full"
            aria-label={`${unreadCount} unread notifications`}
          >
            {unreadCount}
          </span>
        )}
      </div>

      <div className="overflow-y-auto max-h-[280px]">
        {isLoading ? (
          <div className="px-4 py-3 space-y-4" role="status" aria-live="off">
            <span className="sr-only">
              {t("components.notifications.loading", "Loading notifications")}
            </span>

            {[...Array(3)].map((_, idx) => (
              <div key={`${idx}-skeleton`} className="flex gap-3 animate-pulse">
                <div className="mt-2 h-2 w-2 rounded-full bg-gray-200 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-3/4 bg-gray-200 rounded" />
                  <div className="h-2 w-1/2 bg-gray-200 rounded" />
                  <div className="h-4 w-20 bg-gray-200 rounded-full mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center gap-2 py-8 text-sm text-gray-500 text-center"
            role="status"
            aria-live="off"
          >
            <span className="text-base font-medium">
              {t("components.notifications.noDataAvailable")}
            </span>
            <span className="text-xs opacity-70">
              {t("components.notifications.noDataHint", "You don’t have any notifications yet.")}
            </span>
          </div>
        ) : (
          <ul role="list">
            {notifications.map((item, index) => (
              <li key={`${item.id}-${index}`}>
                <div
                  role="group"
                  aria-label={`${item.message}. ${item.time}. ${item.status}${
                    item.unread ? ". Unread notification" : ""
                  }`}
                  className="w-full px-4 py-3 hover:bg-gray-50"
                >
                  <div className="flex gap-3 items-start pointer-events-none">
                    {item.unread && (
                      <>
                        <span
                          className="mt-2 h-2 w-2 rounded-full bg-orange-500 shrink-0"
                          aria-hidden="true"
                        />
                        <span className="sr-only">
                          {t("components.notifications.unread", "Unread")}
                        </span>
                      </>
                    )}

                    <div className="flex-1">
                      <div className="text-sm text-gray-950">{item.message}</div>

                      <div className="text-xs text-gray-400 mt-1">{item.time}</div>

                      <div className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full border border-gray-300 text-gray-700">
                        {item.status}
                      </div>
                    </div>
                  </div>
                </div>

                {index !== notifications.length - 1 && (
                  <div className="mx-4 border-t border-gray-200" aria-hidden="true" />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-gray-200 px-4 py-3 flex justify-center">
        <a
          href={FluxxURL}
          target="_blank"
          rel="noopener noreferrer"
          className={`
            ${
              theme === "theme1"
                ? "w-full inline-flex items-center justify-center gap-2 bg-[var(--color-brand)] text-white text-sm font-medium py-2 rounded-lg transition hover:opacity-90"
                : "inline-flex items-center gap-1 text-sm font-medium text-[var(--color-brand)] hover:underline"
            }
            focus:outline-none
            focus-visible:outline
            focus-visible:outline-2
            focus-visible:outline-[var(--focus-color)]
            focus-visible:outline-offset-2
          `}
          aria-label={t("components.programMetrics.viewInFluxx")}
        >
          {t("components.programMetrics.viewInFluxx")}

          <LinkExternal01
            className={`h-4 w-4 ${theme === "theme1" ? "text-white" : "text-[var(--color-brand)]"}`}
            aria-hidden="true"
          />
        </a>
      </div>
    </div>
  );
};

export default NotificationsDropdown;
